import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import { RoccoLink } from '@rocco/components/link';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import {
    useBookMeetingGateEnabled,
    useOpenBookMeetingGate,
} from '~/modules-rocco/booking/book-meeting-gate-state';
import { PrimaryCtaEnum } from 'generated/graphql';
import { cn } from '@rocco/utils/cn';
import { TileActionPanel } from '~/modules/moodboards/components/TileActionPanel';
import {
    detailCta,
    stripHtml,
    useIslandSection,
    type IslandMode,
} from '~/ui-rocco/pages/detail-shared';
import { professionalTabBase } from '@modules/professional/util/professional-tab-base';
import { buildProfessionalVerifiedLine } from '../utils/professional-trust-signals';
import { ClampedStatement } from './ProfessionalProfileSummarySection';

/**
 * The professional's identity block, sitting on white below
 * {@link import('./ProfessionalProfileHero')}, which carries the name and the
 * discipline, and the tabs: the statement (on Overview, as the desktop
 * summary sets it), the key CTA cluster — Get in touch, Visit Website, Save —
 * and one centred line under it: verified since, views, saves.
 *
 * Modelled on modern marketplace profiles (the stats and CTA move off the
 * banner onto white). The primary enquiry action is prominent here at the top;
 * while it's on screen the header registers a `hidden` island claim so the
 * persistent pill only reappears once the CTA scrolls away.
 */
export const ProfessionalProfileHeader = () => {
    const professional = useProfessionalInitialRouteData();
    const { pathname } = useLocation();
    const { mobile } = useAppDisplayModeContext();
    const { showEnquiryCTA } = useEnquiryVisibility(professional);
    const { openEnquiryModal } = useEnquiryModal({
        professional,
        itemType: 'professional',
        siteTreeID: professional.ID,
        itemTitle: professional.Title,
        includeBranch: true,
        source: 'ProfessionalHeader',
    });
    const enquiryLabel =
        professional.primaryCTA?.primaryCTALabelTitleCase ?? 'Get in touch';

    /*
     * A CTA that says "Book a Consultation" opens the booking flow rather than
     * the generic enquiry form. `AutoOptimise` counts because it resolves to
     * that label — it is the column default, so it is what most profiles show.
     *
     * `useBookMeetingGateEnabled` is `book_meeting_open_access` and
     * `revamp_2026` together, so until the former is turned on this still lands
     * on the enquiry modal, titled as the booking request it is. That is the
     * same fallback the meta row's Book a Meeting already uses.
     */
    const ctaEnum = professional.primaryCTA?.primaryCTA;
    const ctaIsBooking =
        ctaEnum === PrimaryCtaEnum.BookConsultation ||
        ctaEnum === PrimaryCtaEnum.AutoOptimise;
    const bookMeetingEnabled = useBookMeetingGateEnabled();
    const openBookMeetingGate = useOpenBookMeetingGate();

    const onPrimaryCta = () => {
        if (ctaIsBooking && bookMeetingEnabled) {
            openBookMeetingGate({
                professionalUrlSegment: professional.URLSegment,
                siteTreeId: professional.ID,
                professionalName: professional.Title,
            });
            return;
        }
        openEnquiryModal({ title: enquiryLabel, ctaLabel: enquiryLabel });
    };

    // While the header is the section crossing the reading line, claim the
    // island as `hidden` so the persistent enquiry pill doesn't double up with
    // the CTA right here — the pill takes over once the header scrolls past.
    const tabBase = professionalTabBase(professional.URLSegment, pathname);
    const currentPath = pathname.replace(/\/+$/, '');
    // Overview alone carries this block, as the desktop summary does: About's
    // own intro says the practice and its Talk to us holds the ways in, and
    // the listing tabs — Projects, Articles and the rest — open on their
    // grids. Elsewhere it stands down entirely.
    const isOverview = currentPath === tabBase;
    const standsDown = !isOverview;

    const hiddenMode = useMemo<IslandMode>(() => ({ kind: 'hidden' }), []);
    const islandRef = useIslandSection<HTMLElement>(hiddenMode, {
        enabled: mobile && showEnquiryCTA && !standsDown,
        // Beat any in-page section that also wants the island at the top.
        priority: 10,
    });
    // The one line under the ways in: verified since, views, saves.
    const { verifiedSince, views, saves } =
        buildProfessionalVerifiedLine(professional);
    const verifiedLine = [
        verifiedSince ? `ArchiPro Verified since ${verifiedSince}` : null,
        views ? `${views.value} ${views.label}` : null,
        saves ? `${saves.value} ${saves.label}` : null,
    ].filter((part): part is string => part !== null);

    // The statement leads the block on Overview alone, as the desktop
    // summary does: on About it is read again in the section below, and on
    // the listing tabs it stands between the reader and the grid.
    const summary =
        isOverview && professional.BlockSummary
            ? stripHtml(professional.BlockSummary)
            : undefined;

    if (standsDown) return null;

    return (
        <section
            ref={islandRef}
            className="bg-ground max-md:px-6 max-md:pt-6 md:px-[3.125rem] md:pt-[4.5rem]"
        >
            {summary ? (
                // The product page's description, in its reading type:
                // three lines, Read more under them — here a link to the
                // About (/info) tab rather than an expand in place.
                <ClampedStatement
                    text={summary}
                    href={`${tabBase}/info`}
                    className="font-sans text-base leading-[1.4] font-normal text-[#323232]"
                    toggleClassName="text-[#767676]"
                />
            ) : null}
            {/* The ways in, straight after the statement as on the product
                page: full-width primary over a Visit / Save row on mobile; an
                inline row on desktop. */}
            <div className="mt-6 flex gap-2 max-md:mt-7 max-md:flex-col max-md:gap-3 md:mt-8 md:items-center">
                {showEnquiryCTA ? (
                    <button
                        type="button"
                        onClick={onPrimaryCta}
                        // The product page's white lifted pill, at its size.
                        className={detailCta(
                            'white-lifted',
                            'max-md:w-full md:min-w-[16rem]',
                        )}
                    >
                        {enquiryLabel}
                    </button>
                ) : null}
                <div className="flex items-stretch gap-2 max-md:w-full">
                    {professional.Website ? (
                        <RoccoLink
                            to={professional.Website}
                            newTab
                            className={detailCta(
                                'white-lifted',
                                'max-md:flex-1 md:w-[12.1875rem]',
                            )}
                        >
                            Visit Website
                        </RoccoLink>
                    ) : null}
                    {/* ••• in the CTAs' white pill, opening the tile
                        sheet: Share, Save, Save to Pinterest. */}
                    <TileActionPanel
                        pinId={professional.ID}
                        pinType="ProfessionalPin"
                        bareTrigger
                        sheetLayout="page"
                        triggerClassName={cn(
                            detailCta('white-lifted'),
                            'm-0 h-[3.25rem] w-[3.25rem] shrink-0 p-0',
                        )}
                        share={{ url: tabBase, title: professional.Title }}
                        tileInfo={{
                            title: professional.Title,
                            subtitle:
                                professional.PrimaryCategory?.PrettyTitle ??
                                undefined,
                            image: professional.CompanyLogo ?? undefined,
                            imageShape: 'circle',
                            imageBackground:
                                professional.LogoBackgroundColor ?? undefined,
                        }}
                    />
                </div>
            </div>
            {verifiedLine.length > 0 ? (
                // Centred, in the badges' detail type: 13px regular in the
                // muted grey.
                <p className="m-0 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center font-sans text-[0.8125rem] leading-normal font-normal text-[#8a8a8a] max-md:mt-6 md:mt-8">
                    {verifiedLine.map((part, index) => (
                        // The bullet rides with its part, so a wrap on a
                        // narrow phone breaks before a bullet, never after.
                        <span key={part} className="whitespace-nowrap">
                            {index > 0 ? (
                                <span aria-hidden="true" className="mr-2">
                                    &bull;
                                </span>
                            ) : null}
                            {part}
                        </span>
                    ))}
                </p>
            ) : null}
        </section>
    );
};
